/**
 * Lightweight reference parsing for the model layer's ingestion pass.
 *
 * Scope is intentionally narrow: convert A1-style cell and range
 * addresses (with optional $ markers and optional sheet prefix) into
 * structural fields. Anything more complex — full R1C1 grammar, table
 * refs, structured references, 3D spans — is the job of the Phase 8
 * tokenizer, not this file.
 *
 * Today this powers defined-name resolution during catalog build: an
 * `Excel.NamedItem` whose formula is a simple sheet-qualified range
 * (`=Sheet1!$A$1:$B$10`) becomes a `NameEntry`. Names whose formulas
 * are computed expressions return `null` here and are silently skipped
 * by the catalog builder.
 */

/** A1-style column letters → 0-based column index. Returns -1 on invalid input. */
function columnLettersToIndex(letters: string): number {
  if (!letters) return -1;
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    const ch = letters.charCodeAt(i);
    if (ch < 65 || ch > 90) return -1; // uppercase A-Z only
    n = n * 26 + (ch - 64);
  }
  return n - 1;
}

/**
 * Parse a single A1-style cell address (e.g. "A1", "$AB$17") into
 * 0-based (row, col). Returns null on parse failure.
 */
export function parseCellAddress(addr: string): { row: number; col: number } | null {
  const clean = addr.replace(/\$/g, "");
  const m = /^([A-Z]+)(\d+)$/.exec(clean);
  if (!m) return null;
  const col = columnLettersToIndex(m[1]);
  const row = parseInt(m[2], 10) - 1;
  if (col < 0 || row < 0) return null;
  return { row, col };
}

export interface ParsedRangeRef {
  sheetName: string;
  /** 0-based absolute row of the top-left cell. */
  startRow: number;
  /** 0-based absolute column of the top-left cell. */
  startCol: number;
  /** Inclusive row count. */
  rows: number;
  /** Inclusive column count. */
  cols: number;
}

/**
 * Parse a simple sheet-qualified range reference, optionally preceded by
 * `=`. Supported forms:
 *
 *     Sheet1!$A$1
 *     Sheet1!$A$1:$B$10
 *     'Quarterly P&L'!$A$1
 *     'John''s Model'!$A$1:$B$10
 *
 * Returns null for anything else — formula expressions, 3D references,
 * bare references with no sheet prefix, names that resolve via other
 * names, etc.
 */
export function parseSimpleRangeRef(input: string): ParsedRangeRef | null {
  let s = input.trim();
  if (s.startsWith("=")) s = s.slice(1).trim();

  // ---- Sheet name ----
  let sheetName: string;
  let rest: string;

  if (s.startsWith("'")) {
    // Quoted sheet name; '' inside is an escaped apostrophe.
    let i = 1;
    let name = "";
    while (i < s.length) {
      if (s[i] === "'") {
        if (s[i + 1] === "'") {
          name += "'";
          i += 2;
          continue;
        }
        break;
      }
      name += s[i++];
    }
    if (i >= s.length || s[i] !== "'") return null;
    sheetName = name;
    rest = s.slice(i + 1);
  } else {
    const bang = s.indexOf("!");
    if (bang < 0) return null;
    sheetName = s.slice(0, bang);
    rest = s.slice(bang);
  }

  if (!rest.startsWith("!")) return null;
  rest = rest.slice(1);

  // ---- Range body ----
  const parts = rest.split(":");
  if (parts.length === 1) {
    const cell = parseCellAddress(parts[0]);
    if (!cell) return null;
    return { sheetName, startRow: cell.row, startCol: cell.col, rows: 1, cols: 1 };
  }
  if (parts.length === 2) {
    const a = parseCellAddress(parts[0]);
    const b = parseCellAddress(parts[1]);
    if (!a || !b) return null;
    const startRow = Math.min(a.row, b.row);
    const startCol = Math.min(a.col, b.col);
    const endRow = Math.max(a.row, b.row);
    const endCol = Math.max(a.col, b.col);
    return {
      sheetName,
      startRow,
      startCol,
      rows: endRow - startRow + 1,
      cols: endCol - startCol + 1,
    };
  }
  return null;
}
